import type { Client } from '@hero/schema';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';
import { useTranslation } from 'react-i18next';
import {
  AlertCircleIcon,
  BuildingIcon,
  CheckIcon,
  FileTextIcon,
  GlobeIcon,
  PhoneIcon,
  TruckIcon,
  UserIcon,
  ZapIcon,
} from '@hero/web-icons';

// ---------------------------------------------------------------------------
// Section status — derived purely from the Client doc.
// ---------------------------------------------------------------------------

type StatusVariant = 'complete' | 'partial' | 'empty';

export interface SectionStatus {
  variant: StatusVariant;
  /** "3 / 7" for partial sections that have a clear sub-doc field count. */
  countLabel?: string;
  /** 0..1 — used for the completeness bar weighting. */
  filled: number;
}

export interface ClientStatus {
  primaryContact: SectionStatus;
  brand: SectionStatus;
  domain: SectionStatus;
  legal: SectionStatus;
  settings: SectionStatus;
  ai: SectionStatus;
  /**
   * Open agreement (status ∈ `{new, active}`) for the `(org, client)`
   * pair. `presence(true)` when the workspace has fed
   * `hasOpenAgreement: true`, else `presence(false)`. Excluded from
   * `overall` when the caller passes `hasOpenAgreement === undefined`
   * (e.g. the agreement query is still loading or the surface
   * doesn't fetch agreements at all).
   */
  agreement: SectionStatus;
  overall: number;
}

const presence = (hasValue: boolean): SectionStatus => ({
  variant: hasValue ? 'complete' : 'empty',
  filled: hasValue ? 1 : 0,
});

const fraction = (filled: number, total: number): SectionStatus => {
  if (filled === 0) return { variant: 'empty', filled: 0 };
  if (filled >= total) return { variant: 'complete', filled: 1 };
  return {
    variant: 'partial',
    countLabel: `${filled} / ${total}`,
    filled: filled / total,
  };
};

/**
 * A "purely individual" client — `roles` is exactly
 * `['individual_customer']` with no business / carrier / broker
 * overlay. These clients only need a primary contact filled in;
 * domain / legal / settings / agreement aren't relevant for a
 * one-person customer (no CR, no monthly contract envelope).
 *
 * Mixed-role clients (e.g. an individual that's also a `contact`
 * shadow, or anyone with `business_customer` etc. in the array) are
 * NOT treated as individual — they get the full requirement set.
 */
function isIndividualClient(client: Client): boolean {
  const roles = Array.isArray(client.roles) ? client.roles : [];
  return roles.length === 1 && roles[0] === 'individual_customer';
}

export interface ComputeClientStatusOptions {
  /**
   * Whether the (org, client) pair has an open agreement (`status ∈
   * {new, active}`). Async-derived so the caller fetches it via
   * `useAgreementListApi({ partyId: client._id })` and forwards the
   * result. When `undefined` the agreement section is omitted from
   * the `overall` denominator entirely (avoids flickering 0% → 100%
   * during the brief query window). When `true`/`false` it is
   * included.
   */
  hasOpenAgreement?: boolean;
}

export function computeClientStatus(
  client: Client,
  options: ComputeClientStatusOptions = {},
): ClientStatus {
  const { hasOpenAgreement } = options;
  // Primary contact: the sub-doc is optional and so is every field on it
  // (a hauling company recorded for its CR alone has no person at all),
  // so this is a real measurement rather than the constant it used to be.
  // A name and a way to reach them is the whole bar, counted
  // independently because either one can arrive first. `email` stays out
  // of it — penalizing a contact who simply doesn't do email would cap
  // the completeness bar at 93% forever.
  const contact = client.primaryContact;
  const contactFilled = [contact?.name, contact?.phone].filter(Boolean).length;
  const primaryContact = contactFilled > 0 ? fraction(contactFilled, 2) : presence(false);
  const brand = presence(!!client.linkedParty);
  const domain = presence(!!client.domain);

  // Legal completeness is intentionally narrow: only the 5
  // canonical commercial-document fields count. Filling in the
  // address, license, etc. is great but doesn't change the status
  // pill — operators want a clear "the CR + tax docs are on file"
  // signal and shouldn't be nudged toward semi-related fields just
  // to flip the badge to "Complete".
  //
  //   1. registrationName  — CR legal name
  //   2. registrationId    — CR number
  //   3. registrationImage — CR document upload
  //   4. taxId             — Tax / VAT number
  //   5. taxImage          — Tax / VAT document upload
  //
  // `imageWithUploadSchema` allows three shapes (`{id,url}`,
  // `{upload}`, or both); coercing to boolean covers all of them
  // — the field is "filled" as long as we have something stamped.
  const legalFields = [
    client.legal?.registrationName,
    client.legal?.registrationId,
    client.legal?.registrationImage,
    client.legal?.taxId,
    client.legal?.taxImage,
  ];
  const legal = fraction(legalFields.filter(Boolean).length, legalFields.length);

  // Order-settings completeness mirrors the 3 visible rows in the
  // workspace Info tab: cargo, default weight, preferred vehicles.
  // Recipient arrays (`contacts_financial_reports`,
  // `contacts_operation_reports`) are part of the schema but
  // intentionally hidden from the read view + edit modal until the
  // recipients picker UX lands; counting them here would push
  // completeness toward fields the user can't actually see or edit.
  const preferredVehicles = client.settings?.vehicle_preferred_types ?? [];
  const derivedLoadDefaults = preferredVehicles.length
    ? resolveLoadPackingAndQuantityFromVehicles(preferredVehicles)
    : undefined;
  const settingsFields = [
    client.settings?.item_default_name,
    client.settings?.item_default_type,
    client.settings?.item_default_cost,
    client.settings?.item_default_packing ?? derivedLoadDefaults?.packing,
    client.settings?.item_default_quantity ?? derivedLoadDefaults?.quantity,
    client.settings?.item_default_weight,
    client.settings?.vehicle_preferred_types?.length,
    client.settings?.pod_settings,
  ];
  const settings = fraction(settingsFields.filter(Boolean).length, settingsFields.length);

  const ai = presence(!!client.skill);
  // Default to `presence(false)` so consumers that read
  // `status.agreement` (peek rows etc.) always get a `SectionStatus`
  // back. Whether agreement participates in `overall` is decided
  // separately below based on `hasOpenAgreement === undefined`.
  const agreement = presence(hasOpenAgreement === true);

  // Overall completeness — narrow, role-aware allow-list.
  //
  //   - Individual customers (`roles === ['individual_customer']`)
  //     only need a primary contact. Domain / legal / settings /
  //     agreement aren't relevant for a one-person customer, so
  //     including them would permanently cap the bar at ~20%.
  //   - Everyone else (business customers, carriers, brokers, mixed
  //     roles) needs the full envelope: primary contact + agreement
  //     + domain + legal + settings.
  //
  // Brand (`linkedParty`) and AI instructions (`skill`) are
  // intentionally excluded from `overall` — brand is currently
  // hidden from the workspace Info tab anyway, and AI prompts are
  // optional flavor that not every client needs. They still appear
  // as `SectionStatus` entries so peek surfaces can render them.
  // Activity (`lastOrderDate`) was dropped — "this client has
  // shipped before" isn't a setup requirement, it's a side-effect.
  const sectionsForOverall: SectionStatus[] = [primaryContact];
  if (!isIndividualClient(client)) {
    if (hasOpenAgreement !== undefined) sectionsForOverall.push(agreement);
    sectionsForOverall.push(domain, legal, settings);
  }
  const overall = Math.round(
    (sectionsForOverall.reduce((acc, s) => acc + s.filled, 0) / sectionsForOverall.length) * 100,
  );

  return {
    primaryContact,
    brand,
    domain,
    legal,
    settings,
    ai,
    agreement,
    overall,
  };
}

// ---------------------------------------------------------------------------
// Visual atoms
// ---------------------------------------------------------------------------

export function StatusPill({ status }: { status: SectionStatus }) {
  const { t } = useTranslation(['m-client']);

  if (status.variant === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary">
        <CheckIcon className="h-3 w-3" />
        {t('drawer.peek.status.complete')}
      </span>
    );
  }
  if (status.variant === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">
        <AlertCircleIcon className="h-3 w-3" />
        {status.countLabel ?? t('drawer.peek.status.partial')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500">
      {t('drawer.peek.status.empty')}
    </span>
  );
}

export function CompletenessBar({ percent }: { percent: number }) {
  const { t } = useTranslation(['m-client']);
  // Hide the bar entirely once everything required is filled. There's
  // no informational value in showing "100%" — it just adds visual
  // noise above an already-complete profile. Surfaces still call this
  // unconditionally; the gating lives here so every consumer (peek
  // drawer, workspace Info tab, etc.) benefits without per-callsite
  // checks.
  if (percent >= 100) return null;
  const tone = percent >= 80 ? 'bg-primary' : percent >= 40 ? 'bg-amber-500' : 'bg-gray-300';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          {t('drawer.peek.completeness')}
        </span>
        <span className="text-sm font-semibold text-gray-900">{percent}%</span>
      </div>
      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${tone}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

interface PeekRowProps {
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  status: SectionStatus;
  summary?: React.ReactNode;
  empty?: string;
  isLast?: boolean;
}

function PeekRow({ Icon, title, status, summary, empty, isLast }: PeekRowProps) {
  const isEmpty = status.variant === 'empty';
  return (
    <div className={`flex items-start gap-3 py-3 ${isLast ? '' : 'border-b border-gray-100'}`}>
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center text-gray-500">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{title}</span>
          <StatusPill status={status} />
        </div>
        {isEmpty ? (
          <p className="text-sm text-muted-foreground italic truncate">{empty}</p>
        ) : (
          <div className="text-sm text-muted-foreground line-clamp-2">{summary}</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClientPeekInfo — completeness bar + section rows.
//
// Reusable peek body. Used by:
// - drawer Info tab (`ClientSummaryTab`) — wraps with CTA + contacts list
// - workspace Info tab (`ClientWorkspacePage`) — uses on its own
// ---------------------------------------------------------------------------

export interface ClientPeekInfoProps {
  client: Client;
  /** Hide the "Completeness" bar. Useful when the parent already shows it. */
  hideCompleteness?: boolean;
  /** Hide the brand / linked-party row (temporarily disabled in drawer). */
  hideLinkedParty?: boolean;
  /**
   * Forwarded to {@link computeClientStatus} so the agreement row +
   * the overall % stay in sync with the surrounding query state. See
   * `ComputeClientStatusOptions.hasOpenAgreement`.
   */
  hasOpenAgreement?: boolean;
}

export function ClientPeekInfo({
  client,
  hideCompleteness,
  hideLinkedParty,
  hasOpenAgreement,
}: ClientPeekInfoProps) {
  const { t } = useTranslation(['m-client']);
  const status = computeClientStatus(client, { hasOpenAgreement });

  const contact = client.primaryContact;
  // Nothing known about a contact → no summary, so `PeekRow` falls
  // through to its "empty" state instead of rendering a blank name and a
  // lone icon. Every field is optional, so an all-empty sub-doc counts as
  // no contact.
  const hasContact = !!(contact?.name || contact?.phone || contact?.email);
  const contactSummary =
    contact && hasContact ? (
      <div className="space-y-0.5">
        {contact.name ? (
          <div className="flex items-center gap-2 text-sm text-gray-900">
            <span className="font-medium">{contact.name}</span>
            {contact.personType ? (
              <span className="text-xs text-muted-foreground">
                · {contact.personType.replace('_', ' ')}
              </span>
            ) : null}
          </div>
        ) : null}
        {contact.phone ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <PhoneIcon className="h-3 w-3" />
            {contact.phone}
          </div>
        ) : null}
        {contact.email ? (
          <div className="text-xs text-muted-foreground truncate">{contact.email}</div>
        ) : null}
      </div>
    ) : undefined;

  const legalSummary = client.legal
    ? (() => {
        const parts: string[] = [];
        if (client.legal.registrationId) parts.push(`CR ${client.legal.registrationId}`);
        if (client.legal.taxId) parts.push(`Tax ${client.legal.taxId}`);
        if (client.legal.licenseId) parts.push(`License ${client.legal.licenseId}`);
        if (client.legal.addressCity) {
          parts.push(
            `${client.legal.addressCity}${
              client.legal.addressCountry ? `, ${client.legal.addressCountry}` : ''
            }`,
          );
        }
        return parts.length ? parts.join(' · ') : null;
      })()
    : null;

  const settingsSummary = client.settings
    ? (() => {
        const parts: string[] = [];
        if (client.settings.vehicle_preferred_types?.length) {
          parts.push(
            t('drawer.peek.vehicle_types_count', {
              count: client.settings.vehicle_preferred_types.length,
            }),
          );
        }
        if (client.settings.item_default_name) {
          parts.push(client.settings.item_default_name);
        }
        if (client.settings.item_default_weight) {
          parts.push(
            `${client.settings.item_default_weight.value} ${client.settings.item_default_weight.type}`,
          );
        }
        return parts.length ? parts.join(' · ') : null;
      })()
    : null;

  // `CompletenessBar` itself returns `null` at 100% (no value in
  // showing a full bar), so we mirror that here: drop the wrapper
  // spacing + the divider-top above the section rows when the bar
  // wouldn't render. Otherwise the peek body would keep a phantom
  // border-top once the client is fully set up.
  const showCompleteness = !hideCompleteness && status.overall < 100;

  return (
    <div className="flex flex-col gap-3">
      {showCompleteness ? <CompletenessBar percent={status.overall} /> : null}

      <div className={showCompleteness ? 'border-t border-gray-100 pt-1' : ''}>
        <PeekRow
          Icon={UserIcon}
          title={t('drawer.peek.sections.primary_contact')}
          status={status.primaryContact}
          summary={contactSummary}
          empty={t('drawer.peek.empty_states.no_primary_contact')}
        />

        {!hideLinkedParty ? (
          <PeekRow
            Icon={BuildingIcon}
            title={t('drawer.peek.sections.linked_party')}
            status={status.brand}
            summary={
              client.linkedParty ? (
                <span className="text-sm text-gray-900 font-medium">
                  {client.linkedParty.name}
                  {client.linkedParty.target === 'org' ? (
                    <span className="ms-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary uppercase tracking-wide">
                      {t('drawer.peek.link_badge.org')}
                    </span>
                  ) : null}
                </span>
              ) : null
            }
            empty={t('drawer.peek.empty_states.no_brand_linked')}
          />
        ) : null}

        <PeekRow
          Icon={GlobeIcon}
          title={t('drawer.peek.sections.domain')}
          status={status.domain}
          summary={
            client.domain ? <span className="text-sm text-gray-900">{client.domain}</span> : null
          }
          empty={t('drawer.peek.empty_states.no_domain_set')}
        />

        <PeekRow
          Icon={FileTextIcon}
          title={t('drawer.peek.sections.legal')}
          status={status.legal}
          summary={legalSummary}
          empty={t('drawer.peek.empty_states.no_legal_documents')}
        />

        <PeekRow
          Icon={TruckIcon}
          title={t('drawer.peek.sections.order_defaults')}
          status={status.settings}
          summary={settingsSummary}
          empty={t('drawer.peek.empty_states.no_order_defaults')}
        />

        <PeekRow
          Icon={ZapIcon}
          title={t('drawer.peek.sections.ai_instructions')}
          status={status.ai}
          summary={
            client.skill ? (
              <span className="text-sm text-muted-foreground">{client.skill}</span>
            ) : null
          }
          empty={t('drawer.peek.empty_states.no_ai_instructions')}
          isLast
        />
      </div>
    </div>
  );
}
